<script setup lang="ts">
import AsmaAccordion from "@/components/asma/AsmaAccordion.vue";
import AsmaCard from "@/components/asma/AsmaCard.vue";
import InternalLinks from "@/components/seo/InternalLinks.vue";
import { seoConfig } from "@/config/seo.config";
import { asma, type AsmaCategory } from "@/data/asma";
import { computed, onMounted, ref } from "vue";
import asmaChipConfig from "../../../i18n/lang/rn/chips/asma";

const searchQuery = ref("");
const dailyName = ref<(typeof asma)[number] | null>(null);
const activeCategory = ref<"all" | AsmaCategory>("all");

const chips = asmaChipConfig as {
  label: string;
  items: ReadonlyArray<{ key: "all" | AsmaCategory; label: string }>;
};

const hasActiveSearch = computed(() => searchQuery.value.trim().length > 0);

const categoryCounts = computed(() => {
  const counts: Partial<Record<"all" | AsmaCategory, number>> = {};
  for (const name of asma) {
    counts[name.category] = (counts[name.category] ?? 0) + 1;
  }
  return counts;
});

const activeCategoryLabel = computed(() => {
  return (
    chips.items.find((chip) => chip.key === activeCategory.value)?.label ?? ""
  );
});

const dailyReflection = computed(() => {
  const text = dailyName.value?.reflection?.trim();
  if (!text) return "";
  const firstSentence = text.split(/[.!?]/)[0]?.trim();
  return firstSentence || text;
});

function generateDailyName() {
  const today = new Date();
  const seed = today.getFullYear() + today.getMonth() + today.getDate();
  const preferredDailyPool = asma.filter((n) => n.category !== "zindagi");
  const pool = preferredDailyPool.length ? preferredDailyPool : asma;
  const index = seed % pool.length;
  dailyName.value = pool[index];
}

onMounted(() => {
  generateDailyName();
});

useHead({
  title: seoConfig.asma.title,
  meta: [
    { name: "description", content: seoConfig.asma.description },
    {
      name: "keywords",
      content:
        "asma ul husna, 99 names of allah, names of allah with meaning, asma ul husna meanings, allah names and meanings, asma ul husna in english, names of allah in urdu",
    },
    { property: "og:title", content: seoConfig.asma.title },
    { property: "og:description", content: seoConfig.asma.description },
    { property: "og:type", content: "website" },
    { property: "og:url", content: seoConfig.asma.canonical },
    { property: "og:site_name", content: "Rozay" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: seoConfig.asma.title },
    { name: "twitter:description", content: seoConfig.asma.description },
  ],
  link: [{ rel: "canonical", href: seoConfig.asma.canonical }],
});
</script>

<template>
  <section class="w-full max-w-2xl mx-auto px-4 py-4">
    <div class="text-center space-y-1 mb-3">
      <h1
        class="text-2xl font-semibold tracking-tight text-gray-900 dark:text-gray-100"
      >
        99 Names of Allah (Asma ul Husna)
      </h1>      
      <p class="text-sm text-gray-600 dark:text-gray-300 leading-relaxed mt-1">
        Hadith: "Allah ke 99 naam hain, jo unhein yaad karega woh Jannat mein
        dakhil hoga." (Sahih al-Bukhari, Sahih Muslim)
      </p>
    </div>

    <div v-if="!hasActiveSearch" class="flex flex-wrap gap-2 mb-3">
      <button
        v-for="chip in chips.items"
        :key="chip.key"
        class="px-4 py-1.5 rounded-full text-sm whitespace-nowrap"
        :class="
          activeCategory === chip.key
            ? 'bg-gray-800 text-white dark:bg-emerald-500 dark:text-white'
            : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200'
        "
        @click="activeCategory = chip.key"
        :aria-pressed="activeCategory === chip.key"
      >
        {{ chip.label }}
        <span
          class="text-xs ml-1"
          :class="
            activeCategory === chip.key
              ? 'text-gray-200 dark:text-emerald-100'
              : 'text-gray-500 dark:text-gray-400'
          "
        >
          ({{
            chip.key === "all" ? asma.length : (categoryCounts[chip.key] ?? 0)
          }})
        </span>
      </button>
    </div>

    <AsmaCard
      v-if="dailyName"
      :daily-name="dailyName"
      :daily-reflection="dailyReflection"
    />

    <div class="mb-3">
      <input
        v-model="searchQuery"
        type="text"
        placeholder="Allah ka naam search karein..."
        :aria-label="'Search Asma'"
        class="w-full px-4 py-2 rounded-full border border-gray-300 bg-white text-gray-900 placeholder:text-gray-500 text-sm focus:outline-none focus:ring-1 focus:ring-gray-400 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:placeholder:text-gray-400 dark:focus:ring-gray-500"
      />
    </div>

    <AsmaAccordion
      :asma="asma"
      :search-query="searchQuery"
      :active-category="activeCategory"
      :active-category-label="activeCategoryLabel"
    />

    <InternalLinks current="asma" />
  </section>
</template>
